// The attributes a scan identifies a catalog item by: its GTIN and its SKU. The api refuses to save one
// of these onto a second visible item (a scan of it couldn't tell the two apart), so a copy has to leave
// them off. These rules mirror CatalogService.gtinsOf and skusOf in the api; keep them in step.

const SKU_KEY = 'sku';
const GTIN_KEY_WORDS = ['gtin', 'upc', 'ean', 'barcode'];
const MIN_GTIN_DIGITS = 8;
const MAX_GTIN_DIGITS = 14;

// Whether the attribute's name says it holds a GTIN: one of its words is GTIN, UPC, EAN or Barcode, so
// "Case GTIN", "UPC-A" and "EAN13" count but "Clean Date" doesn't
function isGtinKey(key) {
  return (key || '').toLowerCase().split(/[^a-z]+/).some(word => GTIN_KEY_WORDS.includes(word));
}

// Digits only, the length of a GTIN-8 to GTIN-14, and a passing GS1 check digit
function isGtin(value) {
  const digits = (value || '').trim();
  if (!/^\d+$/.test(digits) || digits.length < MIN_GTIN_DIGITS || digits.length > MAX_GTIN_DIGITS) {
    return false;
  }

  const padded = digits.padStart(MAX_GTIN_DIGITS, '0');
  let sum = 0;
  for (let i = 0; i < MAX_GTIN_DIGITS - 1; i++) {
    sum += Number(padded[i]) * (i % 2 === 0 ? 3 : 1);
  }
  return (10 - (sum % 10)) % 10 === Number(padded[MAX_GTIN_DIGITS - 1]);
}

// The item's attributes without its GTINs and SKU, for making a copy of it, plus the keys left off.
// Like the api, only attributes named as GTINs count when the item has any; otherwise any attribute
// holding a valid GTIN does, since some companies keep theirs under a name of their own.
export function withoutScanIdentifiers(catalogMeta) {
  const meta = Array.isArray(catalogMeta) ? catalogMeta : [];
  const hasNamedGtin = meta.some(entry => entry && isGtinKey(entry.key) && isGtin(entry.value));

  const isIdentifier = entry => entry && (
    (entry.key || '').trim().toLowerCase() === SKU_KEY
    || (isGtin(entry.value) && (!hasNamedGtin || isGtinKey(entry.key))));

  return {
    catalogMeta: meta.filter(entry => !isIdentifier(entry)),
    removed: meta.filter(isIdentifier).map(entry => entry.key)
  };
}
